/* eslint-disable prefer-const */
import dayjs from 'dayjs';
import moment from 'moment';
import { TAuthHeader } from 'types/api';
import { APP_LOCAL_DATETIME_FORMAT_NOTIFY } from './constants';
export const PREFIX_UPDATE = 'UPDATE_';
export const PREFIX_LOCAL = 'LOCAL_';
export const PREFIX_HANDLE_INCOMES = [PREFIX_UPDATE, PREFIX_LOCAL];
export const TOKEN_EXPIRED = 'TOKEN_EXPIRED';

export const tokenExpired = (error_message: string, nameStored: string) => {
  if (error_message === TOKEN_EXPIRED) {
    removeStorage(nameStored);
  }
};

export const contentType = (type: string): Record<'Content-Type', string> => {
  return { 'Content-Type': type };
};

export const fixNullToString = <T,>(data: T): T => {
  const rs: Record<string, any> = {};

  for (const v in data) {
    rs[v] = data[v] === null ? '' : data[v];
  }

  return rs as T;
};

export const scrollToStart = () => {
  document?.querySelector("div.ant-table-body")?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
};

export const formatPath = (
  path: string,
  ...params: (string | number)[]
): string => {
  if (!path) {
    return path;
  }

  const match = path.match(/:[a-z][a-z\d_]*\??/gi);
  if (!match) {
    return path;
  }

  match
    .map((param) => param.replace(/\?$/, ''))
    .filter((value, index, self) => self.indexOf(value) === index)
    .map(
      (param, index) =>
        (path = path.replace(
          new RegExp(param + '\\??', 'gi'),
          (params[index] ?? '').toString()
        ))
    );
  return path;
};

export const getAuthHeader = (
  token: string,
  type: TAuthHeader = 'Bearer'
): Record<'Authorization', string> => {
  return { Authorization: `${type} ${token}` };
};

export const getSearchPage = (search: string, name: string): number => {
  const params = new URLSearchParams(search);
  const page = Number(params.get(name)) || 1;
  return page < 1 ? 1 : page;
};

export const clean = (obj: any) => {
  for (const propName in obj) {
    if (
      obj[propName] === null ||
      obj[propName] === undefined ||
      obj[propName] === ''
    ) {
      delete obj[propName];
    } else {
      obj[propName] = String(obj[propName]);
    }
  }
  return obj;
};

export const getQueryString = (
  init: Record<string, string | number | null | undefined | boolean>
) => {
  const initFilter = clean(init);
  const qs: Record<string, string> = {};
  // eslint-disable-next-line array-callback-return
  Object.keys(initFilter).map((k) => {
    const t = typeof initFilter[k];
    switch (t) {
      case 'object':
        qs[k] = '';
        break;
      case 'string':
      case 'number':
        qs[k] = (initFilter[k] as string | number).toString();
        break;
      case 'boolean':
        qs[k] = (initFilter[k] as boolean) ? 'true' : 'false';
        break;
      default:
        break;
    }
  });
  return new URLSearchParams(qs).toString();
};

export const getLocalItem = (name: string) => {
  let data = localStorage.getItem(name);

  try {
    data = JSON.parse(data as string);
  } catch (e) {
    return null;
  }

  return data;
};

export const removeLocalItem = (name: string) => {
  localStorage.removeItem(name);
};

export const setLocalItem = (name: string, data: unknown) => {
  localStorage.setItem(name, JSON.stringify(data));
};

export const getSessionItem = (name: string) => {
  let data = sessionStorage.getItem(name);

  try {
    data = JSON.parse(data as string);
  } catch (e) {
    return null;
  }

  return data;
};

export const setSessionItem = (name: string, data: unknown) => {
  sessionStorage.setItem(name, JSON.stringify(data));
};

export const removeSessionItem = (name: string) => {
  sessionStorage.removeItem(name);
};

export const getStorage = (name: string) => {
  return getLocalItem(name) || getSessionItem(name);
};

export const removeStorage = (name: string, notAuth?: boolean) => {
  removeLocalItem(name);
  removeSessionItem(name);
  notAuth &&
    window.location.pathname !== '/login/' &&
    window.location.replace('/login');
};

export const updateDocumentTitle = (title: string) => {
  const current = document.title.split('|').pop()?.trim();
  const newTitle = [title.toLocaleUpperCase()];
  current && newTitle.push(current);
  document.title = newTitle.join(' | ');
};

export const intToRoman = (original: number): string => {
  if (original < 1 || original > 3999) {
    throw new Error('Error: Input integer limited to 1 through 3,999');
  }

  const numerals = [
    ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'], // 1-9
    ['X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC'], // 10-90
    ['C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM'], // 100-900
    ['M', 'MM', 'MMM'], // 1000-3000
  ];

  const digits = Math.round(original).toString().split('');
  let position = digits.length - 1;

  return digits.reduce((roman, digit) => {
    if (digit !== '0') {
      roman += numerals[position][parseInt(digit) - 1];
    }

    position -= 1;

    return roman;
  }, '');
};

export const getTimeDiffString = (timeStr: string) => {
  const now = dayjs();
  const time = dayjs(timeStr);
  const diff = now.diff(time, 'seconds'); // Convert to seconds
  if (diff < 60) {
    return 'a few seconds ago';
  } else if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} minute${mins > 1 ? 's' : ''} ago`;
  } else if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(diff / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
};

export const converStringDate = (data: string, isHour?: boolean): string => {
  // convert YYYY-MM-DD to DD-MM-YYYY
  if (!data) {
    return '';
  }
  return moment(new Date(data)).format(
    isHour ? 'HH:mm - DD/MM/YYYY' : 'DD/MM/YYYY'
  );
};

export const calcInputNumber = (input: number) => {
  return Number(
    Number.isInteger(input) ? input : parseFloat(`${input}`).toFixed(2)
  );
};

export const removeAccents = (str: string) => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
};

export const pathKeyStore = (param: any) => {
  const values = Object.keys(param).map((key) => param[key]);
  return values.join('_');
};

const acceptFileDocument = '.doc, .docx, .xls, .xlsx, .ppt, .pptx, .pdf, .txt';
const acceptFileImage = '.jpg, .jpeg, .png, .gif, .bmp';

export const renderAccept = (type: string) => {
  let accept = '';
  switch (type) {
    case 'DOCUMENT':
      accept = acceptFileDocument;
      break;
    case 'IMAGE':
      accept = acceptFileImage;
      break;

    default:
      accept = acceptFileDocument + ',' + acceptFileImage;
      break;
  }
  return accept;
};

const fileSizeLimit = 1024;
const limitMB = 25;

export const limitFileSize = (file: File, limit = limitMB) => {
  return file?.size > limit * fileSizeLimit * fileSizeLimit;
};

export const validateFormEmpty = (form_data: any) => {
  const results = Object.values(form_data).some(value => value === "");
  return results;
}

export const create_UUID = () => {
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (dt + Math.random()*16)%16 | 0;
    dt = Math.floor(dt/16);
    return (c === 'x' ? r :(r&0x3|0x8)).toString(16);
  });
  return uuid;
}

export const convertObjectToPath = (step_path: any) => {
  return step_path.map((x: any) => `${x.name}`).join("/");
}

export const formatDateIfNull = (date: string, formatString?: string) => {
  return date !== undefined
    ? dayjs(date).format(formatString || APP_LOCAL_DATETIME_FORMAT_NOTIFY)
    : undefined;
};
